import { usePathname, useSearch as useSearchParams, RouterLink as Link } from "./router";
import { Fragment, useMemo, type ReactNode } from 'react'
import { ChevronRight, MoreHorizontal, Plus, Settings, Star } from "./icons"
import { cn } from './utils'
import { Button } from './button'
import { DropdownMenu } from './dropdown-menu'
import { IconTooltip } from './blocks/icon-tooltip'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'
import { useFavorite } from './favorite-toggle'
import { NavTabs, pickActiveTarget } from './nav-tabs'
import { useShellOwnsTitle } from './shell-title-context'
import type { ContextMenuItem } from './context-menu'

export interface PageNavTab {
 href: string
 label: string
 /** Pre-rendered icon JSX. Server components must instantiate the icon here so
  * the lucide component reference doesn't cross the server→client boundary. */
 icon: ReactNode
 /** Override the path used for active matching (defaults to href). Useful when
  * the tab's link target differs from the path it should highlight on. */
 activeHref?: string
 /** Optional live indicator pinned to the tab's top-right corner — a count
  * bubble ("3 to review"), an alert, or a "working" pulse. Section pages pass a
  * client component here (e.g. `<SectionTabBadge>`) so the inline view buttons
  * react to what's happening in the app. Rendered only for `inlineTabs`. */
 badge?: ReactNode
}

export interface PageNavSection {
 /** Display name of the current section (product, "Excellent", "Settings", …).
  *  Used as the rendered H1 when no explicit `title` or `crumbs` is supplied. */
 label: string
 /** Where the section crumb links to (the section's root route). */
 href: string
}

export interface PageNavCrumb {
 label: string
 href?: string
}

export interface PageNavCreateItem {
 /** Label for the create target (e.g. "New lead", "New opportunity"). */
 label: string
 /** Where the create flow lives (a `/new` route or a page with a create form). */
 href: string
 /** Optional pre-rendered icon JSX shown beside the label in the menu. */
 icon?: ReactNode
}

export interface PageNavProps {
 /** The section this page belongs to. Its `label` is the rendered page title
  *  whenever no explicit `title` / `crumbs` is supplied — the active tab label
  *  is intentionally NOT used as a title source, so the H1 stays anchored to
  *  the section as the user tabs across views. Also used as the back-target
  *  for `FavoriteToggle`. No longer rendered as a breadcrumb. */
 section: PageNavSection
 tabs: PageNavTab[]
 /** Make the page title a link to this href (e.g. a section's dashboard).
  *  The title also participates in active-state matching, so it reads as
  *  selected when the current route is its target. */
 titleHref?: string
 /** Icon-only navigation buttons rendered inline in the title row, to the
  *  left of the page-level overflow menu. Section pages use these to surface
  *  their views (Tasks, Planner, Settings, …) as compact buttons instead of a
  *  separate tab strip. Each `label` becomes the button's tooltip + aria
  *  label. They participate in the same active-state matching as `tabs`. */
 inlineTabs?: PageNavTab[]
 /** Explicit page title. Highest-priority source for the rendered heading.
  *  Use from deep routes whose leaf labels can't be derived from the tab
  *  strip (e.g. `/document/[target]/[doc]`, individual lead/opportunity pages). */
 title?: string
 /** Breadcrumb trail rendered AS the page title. With one entry it reads as a
  *  plain heading; with several it renders the full path (muted parent links →
  *  a chevron → the full-strength current leaf), so the title names exactly
  *  where you are and tracks the active view (`Customers › Board`). The leaf
  *  is the location, so this is the single page title — no separate top-bar
  *  trail. An explicit `title` still wins. */
 crumbs?: PageNavCrumb[]
 /** Optional element rendered to the right of the tabs (e.g. a kbd hint). */
 right?: ReactNode
 /** Optional element rendered before the star slot (typically a scoped,
  *  page-local search button). */
 search?: ReactNode
 /** Optional element rendered between the scoped search slot and the
  *  trailing `right` slot (typically a favorite star toggle). */
 actionsLeading?: ReactNode
 /** Opt-in overflow menu (a kebab "⋯" trigger) that groups page-level
  *  actions — Favorite and (below a separator) Settings — under a single
  *  button. Section pages use this to keep the title row to just the view
  *  buttons + one menu. */
 overflowMenu?: PageNavOverflowMenu
 /** A prominent "+ New" create action rendered first in the title-row action
  *  group (closest to the title). With one item it renders a labeled primary
  *  button linking straight to that create flow; with several it renders a
  *  "New ▾" button that opens a dropdown of create targets. Section pages use
  *  this to surface object creation (New lead / opportunity / sequence / …)
  *  inline with the section name. */
 createMenu?: PageNavCreateItem[]
}

export interface PageNavOverflowMenu {
 /** When set, adds a "Settings" item below a separator (the break between
  *  Settings and the other actions). */
 settingsHref?: string
 /** Label for the settings item. Defaults to "Settings". */
 settingsLabel?: string
 /** Enable the favorite toggle as the first menu item. Pass `{ href }` to
  *  favorite a specific URL instead of the current pathname; `true` uses the
  *  current pathname. */
 favorite?: boolean | { href?: string }
}

/** The shared look of the title-row's compact icon controls — the inline view
 *  buttons (Issues / Planner / Agents …) AND the kebab "⋯" overflow menu, so
 *  they read as one control group rather than two button styles side by side.
 *  `_BASE` is the shape; `_IDLE`/`_ACTIVE` are the two states (the menu is
 *  always idle — it's never a navigation target). */
const NAV_ICON_BTN_BASE =
 'inline-flex size-7 items-center justify-center rounded-full border transition-colors'
const NAV_ICON_BTN_IDLE =
 'border-border bg-background/40 text-muted-foreground hover:text-foreground hover:border-border/80'
const NAV_ICON_BTN_ACTIVE =
 'border-border/80 bg-surface-3 text-foreground shadow-surface-2'

/** Kebab "⋯" overflow menu grouping Favorite and Settings. Rendered only when
 *  `overflowMenu` is set, so the favorites fetch in `useFavorite` never fires
 *  on pages that don't opt in. */
function OverflowMenu({
 favorite,
 settingsHref,
 settingsLabel = 'Settings',
}: {
 favorite?: boolean | { href?: string }
 settingsHref?: string
 settingsLabel?: string
}) {
 const favEnabled = favorite === true || typeof favorite === 'object'
 const favHref = typeof favorite === 'object' ? favorite.href : undefined
 const { favorited, toggle } = useFavorite(favHref, favEnabled)

 const items: ContextMenuItem[] = []
 if (favEnabled) {
 items.push({
 label: favorited ? 'Remove from favorites' : 'Add to favorites',
 icon: <Star className={cn('h-3.5 w-3.5', favorited && 'fill-current')} strokeWidth={1.75} />,
 onSelect: () => {
 void toggle()
 },
 })
 }
 if (settingsHref) {
 items.push({ type: 'separator' })
 items.push({
 label: settingsLabel,
 icon: <Settings className="h-3.5 w-3.5" strokeWidth={1.75} />,
 href: settingsHref,
 })
 }

 return (
 <DropdownMenu items={items} widthPx={184}>
 {/* button-ignore: deliberately shares the inline view-button styling
     (NAV_ICON_BTN_*) so the menu reads as one of the same control group,
     not an outline <Button> with a different fill/ring. */}
 <button
 type="button"
 aria-label="More actions"
 className={cn(NAV_ICON_BTN_BASE, NAV_ICON_BTN_IDLE)}
 >
 <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
 </button>
 </DropdownMenu>
 )
}

export function PageNav({
 section,
 tabs,
 titleHref,
 inlineTabs,
 title,
 crumbs,
 right,
 search,
 actionsLeading,
 overflowMenu,
 createMenu,
}: PageNavProps) {
 const pathname = usePathname()
 const searchParams = useSearchParams()

 const navParams = useMemo(
 () => new URLSearchParams(searchParams?.toString() ?? ''),
 [searchParams],
 )

 const activeTarget = useMemo(() => {
 const targets = [...tabs, ...(inlineTabs ?? [])].map((t) => t.activeHref ?? t.href)
 if (titleHref) targets.push(titleHref)
 return pickActiveTarget(pathname, navParams, targets)
 }, [pathname, navParams, tabs, inlineTabs, titleHref])

 const pageTitle =
 title ??
 crumbs?.[crumbs.length - 1]?.label ??
 section.label

 // A multi-crumb trail (and no explicit single `title`) renders AS the heading:
 // muted parent links → chevron → full-strength leaf. This is the page's one
 // title — it names the exact location and tracks the active view.
 const showTrail = !title && !!crumbs && crumbs.length > 1

 const hasTabs = tabs.length > 0

 // When an enclosing shell (ModeShell) already renders the page title, this
 // PageNav's own heading is a duplicate — defer it. Keep the tabs/actions
 // (a mounted page's view-switcher + toolbar still matter), and drop the
 // sticky title bar so it doesn't fight the shell's sticky title. With
 // nothing left to show, render nothing at all.
 const shellOwnsTitle = useShellOwnsTitle()
 const hasActions = Boolean(
 (createMenu && createMenu.length > 0) ||
 (inlineTabs && inlineTabs.length > 0) ||
 search ||
 actionsLeading ||
 overflowMenu ||
 right,
 )
 if (shellOwnsTitle && !hasActions && !hasTabs) return null

 return (
 <div className={shellOwnsTitle ? 'bg-background' : 'sticky top-0 z-20 bg-background'}>
 {/* Page title + actions */}
 <div
 className={cn(
 'flex min-h-9 items-center gap-2 px-6 pt-3',
 // Extra bottom padding only when a nav (tabs row) sits below the
 // title. On title-only pages (e.g. "Welcome back, Matt") pb-4 reads
 // as too much, so fall back to the tighter pb-2.
 hasTabs ? 'pb-4' : 'pb-2',
 )}
 >
 {shellOwnsTitle ? (
 // The shell owns the heading; keep an empty flex spacer so the
 // action group stays right-aligned in this row.
 <div className="min-w-0 flex-1" aria-hidden />
 ) : (
 <h1 className="flex min-w-0 flex-1 items-center type-page-title">
 {showTrail ? (
 // The breadcrumb-as-title: every crumb but the last is a muted link
 // (the parents/ancestors); the last is the full-strength current page.
 // Parents stay intrinsic-width; only the leaf truncates when space is
 // tight, so you never lose the thing you're actually on.
 crumbs!.map((c, i) => {
 const isLast = i === crumbs!.length - 1
 return (
 <Fragment key={`${c.href ?? c.label}-${i}`}>
 {i > 0 ? (
 <ChevronRight
 className="mx-1 size-4 shrink-0 text-muted-foreground/50"
 strokeWidth={1.75}
 aria-hidden
 />
 ) : null}
 {isLast ? (
 <span className="min-w-0 truncate text-foreground">{c.label}</span>
 ) : c.href ? (
 <Link
 href={c.href}
 className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
 >
 {c.label}
 </Link>
 ) : (
 <span className="shrink-0 text-muted-foreground">{c.label}</span>
 )}
 </Fragment>
 )
 })
 ) : titleHref ? (
 <Link
 href={titleHref}
 aria-current={activeTarget === titleHref ? 'page' : undefined}
 className={cn(
 'truncate transition-colors',
 activeTarget === titleHref
 ? 'text-foreground'
 : 'text-foreground/90 hover:text-foreground',
 )}
 >
 {pageTitle}
 </Link>
 ) : (
 <span className="truncate text-foreground">{pageTitle}</span>
 )}
 </h1>
 )}

 <div className="shrink-0 flex items-center gap-1.5">
 {createMenu && createMenu.length > 0 ? (
 createMenu.length === 1 ? (
 <IconTooltip label={createMenu[0]!.label}>
 <Button asChild variant="default" size="icon-tb" aria-label={createMenu[0]!.label}>
 <Link href={createMenu[0]!.href}>
 <Plus strokeWidth={2.25} />
 </Link>
 </Button>
 </IconTooltip>
 ) : (
 <DropdownMenu
 items={createMenu.map((c) => ({
 label: c.label,
 href: c.href,
 icon: c.icon,
 }))}
 widthPx={196}
 >
 <Button variant="default" size="icon-tb" aria-label="Create new" title="Create new">
 <Plus strokeWidth={2.25} />
 </Button>
 </DropdownMenu>
 )
 ) : null}
 {inlineTabs && inlineTabs.length > 0 ? (
 <TooltipProvider delayDuration={150}>
 {inlineTabs.map((t) => {
 const target = t.activeHref ?? t.href
 const active = target === activeTarget
 return (
 // The relative wrapper anchors the live badge to the button's
 // top-right corner without affecting the button's own layout.
 <span key={t.href} className="relative inline-flex">
 <Tooltip>
 <TooltipTrigger asChild>
 <Link
 href={t.href}
 aria-label={t.label}
 aria-current={active ? 'page' : undefined}
 className={cn(
 NAV_ICON_BTN_BASE,
 active ? NAV_ICON_BTN_ACTIVE : NAV_ICON_BTN_IDLE,
 )}
 >
 {t.icon}
 </Link>
 </TooltipTrigger>
 <TooltipContent side="bottom">{t.label}</TooltipContent>
 </Tooltip>
 {t.badge ? (
 <span className="pointer-events-none absolute -right-1 -top-1 z-10 flex">
 {t.badge}
 </span>
 ) : null}
 </span>
 )
 })}
 </TooltipProvider>
 ) : null}
 {search}
 {actionsLeading}
 {/* A hairline divider sets the page-level menu apart from the inline
     view buttons, so the kebab reads as "everything else" rather than a
     fourth navigation target. */}
 {overflowMenu && inlineTabs && inlineTabs.length > 0 ? (
 <span className="mx-0.5 h-5 w-px bg-border/60" aria-hidden />
 ) : null}
 {overflowMenu ? (
 <OverflowMenu
 favorite={overflowMenu.favorite}
 settingsHref={overflowMenu.settingsHref}
 settingsLabel={overflowMenu.settingsLabel}
 />
 ) : null}
 {right}
 </div>
 </div>

 {/* Tabs row (only when tabs exist) — rendered through the shared NavTabs
     primitive so the tab markup + active matching live in one place. */}
 {hasTabs ? (
 <div className="flex items-center gap-2 px-6">
 <NavTabs
 tabs={tabs}
 activeTarget={activeTarget}
 className="flex-1 min-w-0 overflow-x-auto no-scrollbar"
 />
 </div>
 ) : null}
 </div>
 )
}
